/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片上传预览组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-18 11:14:38
 */

import React, { useState } from 'react'
import { message } from 'antd'
import PhotoPreviewThumbnail, { XCIconAction } from './PhotoPreviewThumbnail'
import {
  LoadingOutlined,
  PlusOutlined,
  WarningOutlined
} from '@ant-design/icons'

interface PhotoUploadPreviewProps {
  imageUrl?: string
  width?: number
  height?: number
  uploading?: boolean
  onPreview?: (imageUrl: string) => void
  onUpload?: (file: any) => void
  onDelete?: () => void
  style?: React.CSSProperties
  showActions?: boolean
  customIconActions?: XCIconAction[]
}

/**
 * 照片上传预览组件
 * 当有图片时显示预览缩略图，支持放大、替换、删除操作
 * 当没有图片时显示上传按钮
 */
const PhotoUploadPreview: React.FC<PhotoUploadPreviewProps> = ({
  imageUrl,
  width = 102,
  height = 102,
  uploading = false,
  onPreview,
  onUpload,
  onDelete,
  style = {},
  showActions = true,
  customIconActions
}) => {
  const [error, setError] = useState<string | null>(null)

  // 处理图标操作
  const handleIconAction = (type: string) => {
    console.log('图标操作类型:', type) // 添加日志，帮助调试

    switch (type) {
      case 'zoom':
        if (imageUrl && onPreview) {
          console.log('调用预览函数，图片URL:', imageUrl)
          onPreview(imageUrl)
        } else {
          console.log('预览函数未定义或图片URL为空', {
            imageUrl,
            onPreview: !!onPreview
          })
        }
        break
      case 'swap':
        console.log('触发替换操作')
        handleUpload()
        break
      case 'delete':
        console.log('触发删除操作')
        if (onDelete) {
          onDelete()
        }
        break
      default:
        // 默认为放大操作
        console.log('默认操作 - 放大图片')
        if (imageUrl && onPreview) {
          onPreview(imageUrl)
        }
        break
    }
  }

  // 处理照片上传
  const handleUpload = () => {
    if (uploading || !onUpload) return

    setError(null) // 重置错误状态

    try {
      const uploadInput = document.createElement('input')
      uploadInput.type = 'file'
      uploadInput.accept = 'image/*'
      uploadInput.onchange = (e) => {
        try {
          // 使用更兼容的方式获取文件
          const target = e.target as HTMLInputElement
          if (!target || !target.files || target.files.length === 0) {
            console.error('未选择文件或文件选择被取消')
            return
          }

          const file = target.files[0]
          if (!file) {
            console.error('文件对象为空')
            return
          }

          console.log('选择了文件:', file.name)

          // 验证文件类型
          const isJpgOrPng =
            file.type === 'image/jpeg' || file.type === 'image/png'
          if (!isJpgOrPng) {
            message.error('只能上传 JPG/PNG 格式的图片!')
            setError('只能上传 JPG/PNG 格式的图片')
            return
          }

          // 验证文件大小
          const isLt2M = file.size / 1024 / 1024 < 2
          if (!isLt2M) {
            message.error('图片大小不能超过 2MB!')
            setError('图片大小不能超过 2MB')
            return
          }

          // 创建符合useUploadImage期望的文件对象格式
          const fileObj = {
            status: 'done',
            originFileObj: file,
            name: file.name
          }

          // 调用上传回调
          onUpload(fileObj)
        } catch (err) {
          console.error('文件处理错误:', err)
          message.error('文件处理失败，请重试')
          setError('文件处理失败')
        }
      }
      uploadInput.click()
    } catch (err) {
      console.error('上传初始化错误:', err)
      message.error('上传初始化失败，请重试')
      setError('上传初始化失败')
    }
  }

  if (imageUrl) {
    // 默认图标配置
    const defaultIconActions: XCIconAction[] = [
      { type: 'zoom', icon: 'fullscreen', position: 'bottom-right' },
      { type: 'swap', icon: 'swap', position: 'bottom-left' },
      { type: 'delete', icon: 'delete', position: 'top-right' }
    ]

    // 使用自定义图标配置或默认配置
    const iconActions = showActions
      ? customIconActions || defaultIconActions
      : []

    // 有图片时，显示预览缩略图
    return (
      <PhotoPreviewThumbnail
        imageUrl={imageUrl}
        width={width}
        height={height}
        style={style}
        onClick={handleIconAction}
        iconActions={iconActions}
        showFullscreenIcon={false} // 使用新的图标配置系统
      />
    )
  } else {
    // 没有图片时，显示上传按钮
    return (
      <div
        style={{
          width,
          height,
          border: '1px dashed #d9d9d9',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
          position: 'relative',
          ...style
        }}
        onClick={!uploading ? handleUpload : undefined}
      >
        <div style={{ textAlign: 'center' }}>
          {error ? (
            <>
              <WarningOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
              <div style={{ marginTop: 8, fontSize: '12px', color: '#ff4d4f' }}>
                上传失败
              </div>
              <div
                style={{ fontSize: '10px', color: '#ff4d4f', maxWidth: '90px' }}
              >
                {error}
              </div>
            </>
          ) : (
            <>
              {uploading ? (
                <LoadingOutlined style={{ fontSize: 20 }} />
              ) : (
                <PlusOutlined style={{ fontSize: 20 }} />
              )}
              <div style={{ marginTop: 8 }}>
                {uploading ? '上传中' : '上传'}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }
}

export default PhotoUploadPreview
